import { useCallback, useEffect, useMemo, useState } from "react";
import { dashFetch } from "@/api/client";
import { Card, Toolbar } from "@/components/Card";
import type {
  BillingAccountSummary,
  BillingPlan,
  BillingSiteSummary,
  CapabilityGroup,
  TenantRow
} from "@/types/account";

const PLAN_HIGHLIGHTS: { id: string; label: string }[] = [
  { id: "edge.cache", label: "Edge cache" },
  { id: "request_log", label: "Request log" },
  { id: "edge.preload_worker", label: "Preload crawls" },
  { id: "deploy", label: "Deploys" },
  { id: "live_users", label: "Live visitors" },
  { id: "recordings", label: "Session recordings" },
  { id: "forms", label: "Forms" },
  { id: "leads", label: "Leads" },
  { id: "ab_tests", label: "A/B tests" },
  { id: "pages", label: "Pages" },
  { id: "firewall", label: "Firewall" },
  { id: "compliance", label: "Compliance" },
  { id: "assistant.chat", label: "AI assistant" },
  { id: "settings.deployment_s3", label: "Custom S3 / CloudFront" }
];

const SALES_HREF = "mailto:hello@sitewrap.io?subject=Sitewrap%20Enterprise%20plan";

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "Custom";
  return `$${Number(v || 0).toLocaleString("en-US")}`;
}

function credits(n: number | undefined): string {
  return Number(n || 0).toLocaleString("en-US");
}

function overage(n: number | undefined): string {
  const v = Number(n || 0);
  if (v <= 0) return "Included";
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: v < 0.1 ? 3 : 2, maximumFractionDigits: 3 })} / extra credit`;
}

function apiError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw) as { error?: string; message?: string };
    const code = String(j.error || "");
    if (code === "plan_not_public") return "That plan is not available for self-serve changes.";
    if (code === "forbidden") return "You do not have permission to change this site’s plan.";
    if (code === "plan_not_found") return "That plan is no longer available.";
    return String(j.message || j.error || raw);
  } catch {
    return raw;
  }
}

function usagePct(site: BillingSiteSummary): number {
  const used = Number(site.plan?.usage?.usedCredits ?? site.creditsTotal ?? 0);
  const included = Number(site.plan?.usage?.includedCredits ?? site.plan?.includedCredits ?? 0);
  if (included <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / included) * 100));
}

export default function BillingPanel({
  sites,
  isAdmin,
  onPlanChanged
}: {
  sites: TenantRow[];
  isAdmin: boolean;
  onPlanChanged: () => Promise<void>;
}) {
  const [summary, setSummary] = useState<BillingAccountSummary | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [groups, setGroups] = useState<CapabilityGroup[]>([]);
  const [targetDomain, setTargetDomain] = useState("");
  const [busyId, setBusyId] = useState("");
  const [msg, setMsg] = useState({ err: "", ok: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, cat] = await Promise.all([
        dashFetch<BillingAccountSummary>("/dashboard/api/account/billing-summary", { cache: "no-store" }),
        dashFetch<{ ok?: boolean; plans?: BillingPlan[]; capabilityGroups?: CapabilityGroup[] }>(
          "/dashboard/api/account/plans-catalog",
          { cache: "no-store" }
        )
      ]);
      if (sum?.ok) setSummary(sum);
      const list = Array.isArray(cat?.plans) ? [...cat.plans] : [];
      list.sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0) || a.name.localeCompare(b.name));
      setPlans(list);
      setGroups(Array.isArray(cat?.capabilityGroups) ? cat.capabilityGroups : []);
    } catch (e) {
      setMsg({ err: apiError(e), ok: "" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const siteRows = useMemo((): BillingSiteSummary[] => {
    const fromSummary = Array.isArray(summary?.sites) ? summary.sites : [];
    if (fromSummary.length) return fromSummary;
    return sites.map((t) => ({
      domain: t.domain,
      creditsTotal: 0,
      plan: { id: t.planId, name: t.planName }
    }));
  }, [summary, sites]);

  useEffect(() => {
    const q = String(new URLSearchParams(location.search).get("site") || "").trim();
    if (q) setTargetDomain(q);
  }, []);

  useEffect(() => {
    if (!targetDomain && siteRows[0]?.domain) setTargetDomain(String(siteRows[0].domain));
  }, [siteRows, targetDomain]);

  const currentPlanId = useMemo(() => {
    const row = siteRows.find((s) => String(s.domain || "").toLowerCase() === targetDomain.toLowerCase());
    return String(row?.plan?.id || sites.find((t) => String(t.domain || "").toLowerCase() === targetDomain.toLowerCase())?.planId || "");
  }, [siteRows, sites, targetDomain]);

  const visiblePlans = useMemo(() => {
    const onBeta = siteRows.some((s) => s.plan?.id === "beta") || sites.some((t) => t.planId === "beta");
    return plans.filter((p) => p.public || isAdmin || (p.id === "beta" && onBeta));
  }, [plans, isAdmin, siteRows, sites]);

  const switchPlan = async (plan: BillingPlan) => {
    if (!targetDomain) {
      setMsg({ err: "Choose a site first.", ok: "" });
      return;
    }
    if (plan.monthlyPriceUsd === null && !isAdmin) return;
    setBusyId(plan.id);
    setMsg({ err: "", ok: "" });
    try {
      const r = await dashFetch<{ ok?: boolean; error?: string; plan?: { name?: string } }>(
        "/dashboard/api/account/site-plan",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ domain: targetDomain, planId: plan.id })
        }
      );
      if (!r?.ok) throw new Error(JSON.stringify(r));
      setMsg({ err: "", ok: `${targetDomain} is now on ${r.plan?.name || plan.name}.` });
      await Promise.all([load(), onPlanChanged()]);
    } catch (e) {
      setMsg({ err: apiError(e), ok: "" });
    } finally {
      setBusyId("");
    }
  };

  const monthLabel = summary?.monthKey || new Date().toISOString().slice(0, 7);

  return (
    <div className="billingLayout">
      <Card title="This month">
        <p className="muted profileLead">
          Credits accrue from cache, bandwidth, crawls, recordings, deploys, and AI usage. Included credits reset each UTC month.
        </p>
        <div className="billingKpis">
          <div className="billingKpi">
            <div className="k">Credits used</div>
            <div className="v">{credits(summary?.totalCredits)}</div>
            <div className="s">{monthLabel}</div>
          </div>
          <div className="billingKpi">
            <div className="k">Sites</div>
            <div className="v">{siteRows.length}</div>
            <div className="s">On this account</div>
          </div>
        </div>
      </Card>

      <Card title="Usage by site">
        {!siteRows.length ? (
          <p className="muted">Add a website to see plan usage and change billing.</p>
        ) : (
          <div className="siteGrid">
            {siteRows.map((s) => {
              const d = String(s.domain || "");
              const used = Number(s.plan?.usage?.usedCredits ?? s.creditsTotal ?? 0);
              const included = Number(s.plan?.usage?.includedCredits ?? s.plan?.includedCredits ?? 0);
              const pct = usagePct(s);
              const over = Number(s.plan?.usage?.overageCredits || 0);
              return (
                <div
                  key={d}
                  className={"siteCard" + (d.toLowerCase() === targetDomain.toLowerCase() ? " isSelected" : "")}
                  role="button"
                  tabIndex={0}
                  onClick={() => setTargetDomain(d)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTargetDomain(d); } }}
                >
                  <div className="d">{d}</div>
                  <div className="o">{s.plan?.name || "No plan"} · {money(s.plan?.monthlyPriceUsd)}/mo</div>
                  <div className="billingBarTrack" aria-hidden="true">
                    <div className={"billingBarFill" + (pct >= 100 ? " over" : "")} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="o mono siteCredits">
                    {credits(used)} / {credits(included)} credits
                    {over > 0 ? ` · ${credits(over)} overage` : ""}
                  </div>
                  {s.error ? <div className="err">{s.error}</div> : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Available plans">
        <Toolbar>
          <p className="muted" style={{ margin: 0, flex: 1 }}>
            Pick a site, then switch to a public plan. Enterprise pricing is custom.
          </p>
          {siteRows.length ? (
            <label className="billingSitePick">
              <span>Change plan for</span>
              <select value={targetDomain} onChange={(e) => setTargetDomain(e.target.value)}>
                {siteRows.map((s) => (
                  <option key={String(s.domain)} value={String(s.domain)}>
                    {s.domain}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </Toolbar>
        {msg.err ? <p className="err">{msg.err}</p> : null}
        {msg.ok ? <p className="ok">{msg.ok}</p> : null}
        {loading && !visiblePlans.length ? <p className="muted">Loading plans…</p> : null}
        <div className="planPriceGrid">
          {visiblePlans.map((plan) => {
            const current = plan.id === currentPlanId;
            const custom = plan.monthlyPriceUsd === null;
            const popular = plan.id === "growth";
            return (
              <div key={plan.id} className={"planPriceCard" + (current ? " isCurrent" : "") + (popular ? " isPopular" : "")}>
                {popular ? <div className="planBadge">Most popular</div> : null}
                {current ? <div className="planBadge current">Current plan</div> : null}
                <h3>{plan.name}</h3>
                <div className="planPrice">
                  {custom ? (
                    <>
                      <span className="amt">Custom</span>
                      <span className="per">Talk to sales</span>
                    </>
                  ) : (
                    <>
                      <span className="amt">{money(plan.monthlyPriceUsd)}</span>
                      <span className="per">per site / month</span>
                    </>
                  )}
                </div>
                <p className="planDesc">{plan.description}</p>
                <ul className="planFacts">
                  <li>{credits(plan.includedCredits)} credits included</li>
                  <li>{overage(plan.overageUsdPerCredit)}</li>
                  {plan.beta ? <li>Internal / early access</li> : null}
                </ul>
                <ul className="planCaps">
                  {PLAN_HIGHLIGHTS.map((h) => {
                    const on = plan.capabilities?.[h.id] === true;
                    return (
                      <li key={h.id} className={on ? "on" : "off"}>
                        <span aria-hidden="true">{on ? "✓" : "–"}</span>
                        {h.label}
                      </li>
                    );
                  })}
                </ul>
                {custom && !isAdmin ? (
                  <a className="btn primary" href={SALES_HREF}>Talk to sales</a>
                ) : (
                  <button
                    type="button"
                    className={"btn" + (current ? "" : " primary")}
                    disabled={current || !targetDomain || busyId === plan.id}
                    onClick={() => void switchPlan(plan)}
                  >
                    {current ? "Current plan" : busyId === plan.id ? "Switching…" : `Switch to ${plan.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {groups.length ? (
          <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>
            Feature lists show the capabilities most operators compare. Administrators can edit the full catalog on the Plans tab.
          </p>
        ) : null}
      </Card>
    </div>
  );
}
